/**
 * `/settings` — the account, not the ring. Add another passkey, link GitHub,
 * sign out.
 *
 * The API has no endpoint that lists a user's credentials, so this page shows
 * what it can prove: who you are signed in as, and how to add another way in.
 */
import type { Invite, User } from '@circle/shared';
import { inviteLink } from '@circle/shared';
import { api } from '../api';
import {
  browserSupportsWebAuthn,
  githubEnabled,
  githubSignInUrl,
  inviteAllowance,
  invitesRequired,
  loadAppConfig,
  passkeyErrorMessage,
  rememberGithubSignIn,
  registerPasskey,
} from '../auth';
import type { PageContext, Teardown } from '../router';
import { button, copyRow, el, setChildren, textField, withBusy } from '../../ui/dom';
import { confirmDialog } from '../../ui/dialog';
import { loadingState } from '../../ui/states';
import { errorMessage, toast, toastError } from '../../ui/toast';

export async function renderSettingsPage(ctx: PageContext): Promise<Teardown> {
  ctx.host.appendChild(loadingState('Loading your account'));
  await loadAppConfig();
  const me = await api.me().catch(() => null);

  const col = el('div', { class: 'sheet__col' });
  ctx.host.replaceChildren(el('div', { class: 'sheet' }, col));

  if (!me) {
    col.append(
      el('h1', { text: 'Not signed in' }),
      el('p', {
        class: 'lede',
        text: 'Calendars you hold by a link still work without an account. Sign in to reach them from any device.',
      }),
      el(
        'div',
        { class: 'row' },
        el('a', { class: 'btn', href: '/', text: 'Back to your calendars' }),
        githubEnabled()
          ? el('a', { class: 'btn', href: githubSignInUrl('/settings'), rel: 'external', text: 'Sign in with GitHub', onClick: rememberGithubSignIn })
          : null,
      ),
    );
    return;
  }

  let user: User = me.user;

  // The name is the one thing an account has of its own, and it is what a
  // passkey was registered under — so it has to be fixable here.
  const heading = el('h1', { text: user.name });
  const nameField = textField({
    label: 'Your name',
    name: 'display-name',
    value: user.name,
    maxlength: '60',
    autocomplete: 'name',
    hint: 'Shown on your passkeys and on this page. Nobody else sees it.',
  });
  const saveName = button(
    'Save',
    () =>
      void withBusy(saveName, async () => {
        const next = nameField.input.value.trim();
        if (!next) {
          nameField.setError('A name, however short.');
          return;
        }
        if (next === user.name) {
          nameField.setError(null);
          return;
        }
        try {
          const updated = await api.patchMe(next);
          user = updated.user;
          nameField.setError(null);
          nameField.input.value = user.name;
          heading.textContent = user.name;
          toast('Name saved.');
        } catch (err) {
          nameField.setError(errorMessage(err, 'That name did not save.'));
        }
      }),
    { class: 'btn--small' },
  );
  nameField.input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveName.click();
    }
  });

  const deleteAccount = button(
    'Delete account',
    async () => {
      const yes = await confirmDialog({
        title: 'Delete this account?',
        description:
          'Your passkeys and sign-ins go for good, and there is no way back into the account. Calendars you still own have to go first, or be handed on with their owner links.',
        confirmLabel: 'Delete account',
        danger: true,
      });
      if (!yes) return;
      try {
        await api.deleteMe();
        toast('Account deleted.');
        ctx.navigate('/');
      } catch (err) {
        toastError(err, 'The account was not deleted.');
      }
    },
    { class: 'btn--small btn--danger' },
  );

  const addPasskey = button(
    'Add a passkey',
    () =>
      void withBusy(addPasskey, async () => {
        try {
          await registerPasskey(user.name);
          toast('Passkey added. You can sign in with it on this device.');
        } catch (err) {
          toast(passkeyErrorMessage(err), 'error');
        }
      }),
  );

  // --- invitations ---------------------------------------------------------
  //
  // The allowance only exists for someone who already has a ring of their own,
  // which is what the worker checks too; for anyone else the section would be a
  // button that 403s.
  const invitesBody = el('div', { class: 'stack' });
  let invites: Invite[] = [];

  const mint = button(
    'Create an invitation',
    () =>
      void withBusy(mint, async () => {
        try {
          const invite = await api.mintInvite();
          invites = [invite, ...invites];
          paintInvites();
          toast('Invitation ready. Send the link to one person.');
        } catch (err) {
          toastError(err, 'That invitation was not created.');
        }
      }),
    { class: 'btn--small' },
  );

  function inviteState(invite: Invite): string {
    if (invite.revokedAt) return 'revoked';
    if (invite.uses >= invite.maxUses) return 'used';
    if (invite.expiresAt && Date.parse(invite.expiresAt) < Date.now()) return 'expired';
    return 'not used yet';
  }

  function inviteRow(invite: Invite): HTMLElement {
    const spent = Boolean(invite.revokedAt) || invite.uses >= invite.maxUses;
    const revoke = button(
      'Revoke',
      () =>
        void withBusy(revoke, async () => {
          try {
            const updated = await api.revokeInvite(invite.id);
            invites = invites.map((i) => (i.id === updated.id ? updated : i));
            paintInvites();
            toast('Invitation revoked. Anything already made with it stays.');
          } catch (err) {
            toastError(err, 'That invitation was not revoked.');
          }
        }),
      { class: 'btn--small' },
    );
    return el(
      'li',
      { class: 'stack', style: 'padding:12px 4px' },
      el(
        'div',
        { class: 'row' },
        el('span', { class: 'list__name mono', text: invite.code }),
        el('span', { class: 'list__meta', text: inviteState(invite) }),
        spent ? null : revoke,
      ),
      spent ? null : copyRow(inviteLink(invite.code, location.origin), 'Copy link'),
    );
  }

  function paintInvites(): void {
    const live = invites.filter((i) => !i.revokedAt).length;
    const left = Math.max(inviteAllowance() - live, 0);
    setChildren(invitesBody, [
      el('p', {
        class: 'small muted',
        text: invitesRequired()
          ? `Circles is invitation-only. You have ${left} of ${inviteAllowance()} left — each one lets one person make a calendar of their own.`
          : `Anyone can make a calendar here at the moment, so nobody needs one of these. You have ${left} of ${inviteAllowance()} left.`,
      }),
      el(
        'div',
        { class: 'row' },
        left > 0
          ? mint
          : el('span', {
              class: 'small muted',
              text: 'None left. Revoke one that has not been used to free it up.',
            }),
      ),
      invites.length
        ? el('ul', { class: 'cal-list' }, ...invites.map(inviteRow))
        : el('div', { class: 'empty', text: 'None yet.' }),
    ]);
  }

  const signOut = button(
    'Sign out',
    () =>
      void withBusy(signOut, async () => {
        try {
          await api.logout();
          ctx.navigate('/');
        } catch (err) {
          toastError(err);
        }
      }),
    { class: 'btn--quiet' },
  );

  col.append(
    el(
      'div',
      { class: 'stack', style: 'gap:8px' },
      heading,
      el('p', {
        class: 'lede',
        text:
          me.calendars.length === 1
            ? '1 calendar belongs to this account.'
            : `${me.calendars.length} calendars belong to this account.`,
      }),
    ),
    el(
      'section',
      { class: 'section' },
      el('h2', { text: 'Your name' }),
      el('div', { class: 'row' }, nameField.root, saveName),
    ),
    el(
      'section',
      { class: 'section' },
      el('h2', { text: 'Ways to sign in' }),
      el('p', {
        class: 'small muted',
        text: 'Add a passkey on every device you use. Each one works on its own, so losing a device does not lock you out.',
      }),
      el(
        'div',
        { class: 'row' },
        browserSupportsWebAuthn()
          ? addPasskey
          : el('span', { class: 'small muted', text: 'This browser cannot make passkeys.' }),
        githubEnabled()
          ? el('a', {
              class: 'btn',
              href: githubSignInUrl('/settings'),
              rel: 'external',
              text: 'Link GitHub',
              onClick: rememberGithubSignIn,
            })
          : null,
      ),
    ),
    ...(me.calendars.length
      ? [el('section', { class: 'section' }, el('h2', { text: 'Invitations' }), invitesBody)]
      : []),
    el(
      'section',
      { class: 'section' },
      el('h2', { text: 'Your calendars' }),
      me.calendars.length
        ? el(
            'ul',
            { class: 'cal-list' },
            ...me.calendars.map((calendar) =>
              el(
                'li',
                {},
                el(
                  'a',
                  { href: `/circles/${calendar.id}` },
                  el('span', { class: 'list__name', text: calendar.name }),
                  el('span', { class: 'list__meta', text: calendar.visibility === 'private' ? 'private' : 'shared by link' }),
                ),
              ),
            ),
          )
        : el('div', { class: 'empty', text: 'None yet.' }),
    ),
    el('hr', { class: 'rule' }),
    el('div', { class: 'row' }, el('a', { class: 'btn btn--quiet', href: '/', text: 'Back' }), signOut),
    el(
      'section',
      { class: 'section' },
      el('h2', { text: 'Close the account' }),
      el('p', {
        class: 'small muted',
        text: me.calendars.length
          ? 'Delete or hand on the calendars above first — this account can only go once nothing is attached to it.'
          : 'Removes your passkeys and sign-ins. It cannot be undone.',
      }),
      el('div', { class: 'row' }, deleteAccount),
    ),
  );

  if (me.calendars.length) {
    paintInvites();
    void api
      .listInvites()
      .then((list) => {
        invites = list;
        paintInvites();
      })
      .catch(() => {
        setChildren(invitesBody, [
          el('div', { class: 'empty', text: 'Your invitations could not be loaded.' }),
        ]);
      });
  }

  return;
}
